import { SubmissionError, change } from 'redux-form';
/* eslint-disable no-param-reassign */
/* eslint-disable no-underscore-dangle */
import { call, put, select, takeLatest } from 'redux-saga/effects';
import {
  closeDeleteVideoModal,
  closeEditVideoModal,
  closeModal,
  closeSelectVideoModal,
} from 'modules/modal/actions';
import { messageError, messageSuccess } from 'views/ui/toast-message';

import SDK from 'utils/sdk';
import { getSelectedStation } from 'modules/stations/selectors';
import { getVideoConsoleFormStations } from 'modules/form/video-console/selector';
import { push } from 'connected-react-router';
import recursivelyStripNullValues from 'utils/strip-null-values';
import {
  getPagination,
  getSearchVideosEquipment,
  getSearchVideosFocusArea,
  getSearchVideosTerm,
} from './selectors';
import {
  deleteVideo,
  editVideo,
  getEquipments,
  getFocusAreas,
  getVideos,
  selectVideo,
  uploadVideo,
} from './actions';

export function* uploadVideoSaga({ payload: { values: requestBody } }) {
  try {
    yield put(uploadVideo.request());

    const formData = new FormData();
    Object.entries(requestBody).forEach(([key, value]) =>
      formData.append(key, value)
    );

    const client = yield call([SDK, 'getClient']);
    yield call([client, 'videoUpload'], null, formData, {
      headers: { 'content-type': 'multipart/form-data' },
    });

    yield put(closeModal());
    yield put(getVideos());
    messageSuccess('The video has been uploaded correctly!');
    yield put(push('/app/library'));
  } catch (error) {
    const data = error?.response?.data?.message?.toString();
    yield put(
      uploadVideo.failure(
        new SubmissionError({
          _error: data,
        })
      )
    );
    messageError(data);
  } finally {
    yield put(uploadVideo.fulfill());
  }
}

export function* selectVideoSaga({ payload: { values } }) {
  try {
    yield put(selectVideo.request());

    const { station, group } = yield select(getSelectedStation);
    const stations = yield select(getVideoConsoleFormStations);

    yield put(
      change('video-console', 'stations', {
        ...stations,
        [station]: group
          ? {
              ...stations[station],
              [group]: values.videoElement,
            }
          : values.videoElement,
      })
    );

    yield put(closeSelectVideoModal());

    yield put(push('/app/video-console'));
  } catch (error) {
    const data = error?.response?.data?.message?.toString();
    yield put(
      selectVideo.failure(
        new SubmissionError({
          _error: data,
        })
      )
    );
  } finally {
    yield put(selectVideo.fulfill());
  }
}

export function* getVideosSaga() {
  try {
    yield put(getVideos.request());

    const pagination = yield select(getPagination);

    const searchTerm = yield select(getSearchVideosTerm);
    const searchFocusArea = yield select(getSearchVideosFocusArea);
    const searchEquipment = yield select(getSearchVideosEquipment);

    const params = recursivelyStripNullValues(pagination);

    const requestBody = {
      exercise_name: searchTerm === '' ? null : searchTerm,
      equipment: searchEquipment?.toUpperCase(),
      focus_area: searchFocusArea?.toUpperCase(),
    };

    const client = yield call([SDK, 'getClient']);
    const { data } = yield call(
      [client, 'videoListSearch'],
      params,
      requestBody
    );
    yield put(getVideos.success(data));
  } catch (error) {
    const data = error?.response?.data?.message?.toString();

    yield put(
      getVideos.failure(
        new SubmissionError({
          _error: data,
        })
      )
    );
    messageError(data || 'Could not load the videos');
  } finally {
    yield put(getVideos.fulfill());
  }
}

export function* getFocusAreasSaga() {
  try {
    yield put(getFocusAreas.request());
    const client = yield call([SDK, 'getClient']);
    const { data } = yield call([client, 'focusAreaList']);

    yield put(getFocusAreas.success(data));
  } catch (error) {
    const data = error?.response?.data?.message?.toString();

    yield put(
      getFocusAreas.failure(
        new SubmissionError({
          _error: data,
        })
      )
    );
  } finally {
    yield put(getFocusAreas.fulfill());
  }
}

export function* getEquipmentsSaga() {
  try {
    yield put(getEquipments.request());
    const client = yield call([SDK, 'getClient']);
    const { data } = yield call([client, 'equipmentList']);

    yield put(getEquipments.success(data));
  } catch (error) {
    const data = error?.response?.data?.message?.toString();

    yield put(
      getEquipments.failure(
        new SubmissionError({
          _error: data,
        })
      )
    );
  } finally {
    yield put(getEquipments.fulfill());
  }
}

export function* editVideoSaga({ payload: { values } }) {
  try {
    yield put(editVideo.request());
    const { id, ...requestBody } = values;
    const params = { id };

    const client = yield call([SDK, 'getClient']);
    yield call([client, 'videoUpdate'], params, requestBody);

    yield put(closeEditVideoModal());
    yield put(getVideos());
  } catch (error) {
    const data = error?.response?.data?.message?.toString();
    yield put(
      editVideo.failure(
        new SubmissionError({
          _error: data,
        })
      )
    );
  } finally {
    yield put(editVideo.fulfill());
  }
}

export function* deleteVideoSaga({ payload: { values } }) {
  try {
    yield put(deleteVideo.request());

    const params = { id: values.id };

    const client = yield call([SDK, 'getClient']);
    const { data } = yield call([client, 'videoDelete'], params, {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });

    if (data.deletedCount === 1) {
      messageSuccess('Video Deleted!');
    }

    yield put(getVideos());
  } catch (error) {
    const data = error?.response?.data?.message?.toString();

    yield put(
      deleteVideo.failure(
        new SubmissionError({
          _error: data,
        })
      )
    );
    messageError(data);
  } finally {
    yield put(closeDeleteVideoModal());
    yield put(deleteVideo.fulfill());
  }
}

export default function* videoWatch() {
  yield takeLatest(uploadVideo.TRIGGER, uploadVideoSaga);
  yield takeLatest(selectVideo.TRIGGER, selectVideoSaga);
  yield takeLatest(editVideo.TRIGGER, editVideoSaga);
  yield takeLatest(deleteVideo.TRIGGER, deleteVideoSaga);

  yield takeLatest(getVideos.TRIGGER, getVideosSaga);
  yield takeLatest(getFocusAreas.TRIGGER, getFocusAreasSaga);
  yield takeLatest(getEquipments.TRIGGER, getEquipmentsSaga);
}
